/**
 * Lab Parlay Hooks
 * Hooks for parlay performance, parlay dashboard, and parlay insights
 */

import { API_BASE_URL } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { type ApiErrorResponse, getUserFriendlyError, safeParseJson } from '@utils/api';
import { useCallback, useEffect, useState } from 'react';
import type {
  DailyParlayPerformance,
  LabFilters,
  ParlayDashboardData,
  ParlayInsights,
} from './labDashboardTypes';

// =============================================================================
// Result Types
// =============================================================================

export interface UseParlayPerformanceResult {
  data: DailyParlayPerformance[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export interface UseParlayDashboardResult {
  data: ParlayDashboardData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export interface UseParlayInsightsResult {
  data: ParlayInsights | null;
  loading: boolean;
  error: string | null;
  recentDays: number;
  setRecentDays: (days: number) => void;
  refresh: () => Promise<void>;
}

// =============================================================================
// useParlayPerformance
// =============================================================================

export function useParlayPerformance(
  filters: LabFilters,
  limit: number = 30,
): UseParlayPerformanceResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [data, setData] = useState<DailyParlayPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchParlayPerformance = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }
      if (filters.scope && filters.scope !== 'all') {
        params.set('scope', filters.scope);
      }
      if (filters.scope === 'user' && filters.userId) {
        params.set('userId', filters.userId);
      }
      params.set('limit', limit.toString());

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/lab/parlay-performance${queryString ? `?${queryString}` : ''}`;

      const response = await authFetch(url);

      const result = await safeParseJson<
        { parlayPerformance: DailyParlayPerformance[] } | ApiErrorResponse
      >(response);

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      setData((result as { parlayPerformance: DailyParlayPerformance[] }).parlayPerformance);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch parlay performance';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, filters, limit]);

  useEffect(() => {
    fetchParlayPerformance();
  }, [fetchParlayPerformance]);

  return { data, loading, error, refresh: fetchParlayPerformance };
}

// =============================================================================
// useParlayDashboard
// =============================================================================

export function useParlayDashboard(filters: LabFilters): UseParlayDashboardResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [data, setData] = useState<ParlayDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchParlayDashboard = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }
      if (filters.scope && filters.scope !== 'all') {
        params.set('scope', filters.scope);
      }
      if (filters.scope === 'user' && filters.userId) {
        params.set('userId', filters.userId);
      }

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/lab/parlay-dashboard${queryString ? `?${queryString}` : ''}`;

      const response = await authFetch(url);

      const result = await safeParseJson<ParlayDashboardData | ApiErrorResponse>(response);

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      setData(result as ParlayDashboardData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch parlay dashboard';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, filters]);

  useEffect(() => {
    fetchParlayDashboard();
  }, [fetchParlayDashboard]);

  return { data, loading, error, refresh: fetchParlayDashboard };
}

// =============================================================================
// useParlayInsights
// =============================================================================

export function useParlayInsights(filters: LabFilters): UseParlayInsightsResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [data, setData] = useState<ParlayInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentDays, setRecentDays] = useState(7);

  const fetchParlayInsights = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }
      if (filters.scope && filters.scope !== 'all') {
        params.set('scope', filters.scope);
      }
      if (filters.scope === 'user' && filters.userId) {
        params.set('userId', filters.userId);
      }
      params.set('recentDays', recentDays.toString());

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/lab/parlay-insights${queryString ? `?${queryString}` : ''}`;

      const response = await authFetch(url);

      const result = await safeParseJson<ParlayInsights | ApiErrorResponse>(response);

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      setData(result as ParlayInsights);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch parlay insights';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, filters, recentDays]);

  useEffect(() => {
    fetchParlayInsights();
  }, [fetchParlayInsights]);

  return {
    data,
    loading,
    error,
    recentDays,
    setRecentDays,
    refresh: fetchParlayInsights,
  };
}
